import React, { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  Input,
  Form,
  Select,
  Row,
  Col,
  Spin,
  Tooltip,
  Tabs,
  Image,
  message
} from "antd";
import {
  PlusOutlined,
  QuestionCircleOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import UserIcon from "../../../../../../assets/images/icons/userLogo.svg";
import { useLocation, useNavigate } from "react-router-dom";
import { useStore } from "../../../../../../store";
import { aboutUSkeys, TAB_KEYS } from "../../../../../../config";
import { useTab } from "../../../../../../custom-hooks/useTab";
import { useConnectsuForm } from "../../../../../../custom-hooks/useConnectsuForm";
import { getFormFieldValue } from "../../../../../../utils";
import ReactPlayer from "react-player/youtube";
import SynthesiaVideo from "../../SynthesiaVideo";

const { TextArea } = Input;
const MAX_CHARACTERS = 2000;

export default function OurStoryComponent({
  dataLoader,
  form,
  activeKey,
}) {
  const { state } = useLocation();
  const tabKey = state;

  const { tabData } = useStore();
  const whichTab = aboutUSkeys["Our Story"];
  const { deleteSingleItem, postLoader } = useTab();
  const { updateImage } = useConnectsuForm();
  const [loader, setLoader] = useState(true);
  const [formChanged, setFormChanged] = useState(false);
  const { updatedValues } = useStore();
  const [canAddNew, setCanAddNew] = useState(true);
  const [hasUnsavedItem, setHasUnsavedItem] = useState(false);

  if (activeKey === "OUR_STORY") {
    localStorage.setItem("tab", activeKey);
  }

  useEffect(() => {
    setFormChanged(false);
    setCanAddNew(true);
  }, []);

  useEffect(() => {
    const isNotEmpty = Object.keys(updatedValues).length > 0;
    setFormChanged(isNotEmpty);
  }, [Object.keys(updatedValues).length]);


  const renderMedia = (dataItem) => {
    const commonStyles = {
      width: "350px",
      height: "250px",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
    };

    const mediaType = dataItem?.mediaLink?.mediaType;
    const mediaUrl =
      dataItem?.mediaLink?.url || // 👈 form state
      dataItem?.aboutUsTeamDataOutPut?.image?.url; // 👈 backend fallback

    if (!mediaUrl) return null;

    if (mediaType === "SOCIAL_MEDIA") {
      return (
        <div className="iframe-comp" style={commonStyles}>
          <ReactPlayer
            controls={true}
            url={mediaUrl}
            width="100%"
            height="100%"
          />
        </div>
      );
    } else if (mediaType === "VIMEO_MEDIA") {
      return (
        <div className="iframe-comp" style={commonStyles}>
          <iframe
            src={mediaUrl}
            frameBorder="0"
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            style={{ width: "100%", height: "100%", backgroundColor: "black" }}
          />
        </div>
      );
    } else if (mediaType === "IMAGE" || !mediaType) {
      // default/fallback: IMAGE or no type provided
      return (
        <div className="iframe-comp" style={commonStyles}>
          <img
            src={mediaUrl}
            alt="media"
            className="c1 carousel-image"
            style={{ maxWidth: "100%", maxHeight: "100%" }}
          />
        </div>
      );
    }
    else if (
      dataItem?.mediaLink?.mediaType === "SYNTHESIA_MEDIA" &&
      dataItem.mediaLink.url) {
      return (
        <div>
          <SynthesiaVideo key={dataItem.id} videoData={dataItem} />

        </div>
      )
    }


    return null;
  };


  const findValue = (i, key) => {
    return getFormFieldValue(form, whichTab, i, key);
  };


  const SelectChangeHandler = (e, i) => {
    form.setFieldValue([whichTab, i, "mediaLink", "url"], "");
  };


  const handleAddMore = () => {
    if (!canAddNew) {
      message.warning("Please save or clear the current item before adding a new one.");
      return;
    }

    const currentList = form.getFieldValue(whichTab) || [];

    // Check if any unsaved row already has data filled
    const unsavedItem = currentList.find((item) => {
      const hasData =
        item?.title?.trim() ||
        item?.bio?.trim() ||
        item?.linkedin?.trim() ||
        item?.email?.trim() ||
        item?.mediaLink?.url ||   // image/video url
        item?.media;              // blob
      return !item?.id && hasData;
    });

    if (unsavedItem) {
      message.warning("Please save or clear the current item before adding a new one.");
      return;
    }

    // ✅ Prepend new empty row at the top (aligned with your fields)
    const newList = [
      {
        title: "",
        bio: "",
        linkedin: "",
        email: "",
        mediaLink: { mediaType: undefined, url: "" },
        media: null,
      },
      ...currentList,
    ];

    form.setFieldsValue({ [whichTab]: newList });

    setCanAddNew(false);
    setHasUnsavedItem(true);

    // Scroll to top to focus on the newly added row
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSave = () => {
    form.submit();

    // After submit succeeds, unlock adding new items
    setTimeout(() => {
      setCanAddNew(true);
      setHasUnsavedItem(false);
    }, 500);
  };

  const refreshUnsavedState = () => {
    const currentList = form.getFieldValue(whichTab) || [];
    const unsavedItem = currentList.find(item =>
      !item?.id &&
      (item?.title?.trim() || item?.description?.trim() || item?.mediaLink?.trim() || item?.media?.trim())
    );
    setCanAddNew(!unsavedItem);
  };

  const handleClear = (fields, remove) => {
    const currentList = form.getFieldValue(whichTab) || [];

    // 1️⃣ Remove newly added unsaved rows except the first
    const itemsToRemove = [];
    fields.forEach((f, i) => {
      if (!findValue(i, "id") && i !== 0) {
        itemsToRemove.push(f.name);
      }
    });
    if (itemsToRemove.length > 0) {
      remove(itemsToRemove);
    }

    // 2️⃣ Reset first unsaved row fields if any
    if (currentList.length > 0) {
      const updatedList = [...currentList];
      const clearIndex = updatedList.findIndex((item) => !item?.id);

      if (clearIndex !== -1) {
        updatedList[clearIndex] = {
          title: "",
          description: "",
          mediaLink: { mediaType: undefined, url: "" },
          media: null,
        };
      }

      form.setFieldsValue({ [whichTab]: updatedList });
    }

    // 3️⃣ Safe unsaved check (no trim errors)
    const unsavedItem = (form.getFieldValue(whichTab) || []).find((item) => {
      const title = typeof item?.title === "string" ? item.title.trim() : "";
      const desc = typeof item?.description === "string" ? item.description.trim() : "";
      const url = typeof item?.mediaLink?.url === "string" ? item.mediaLink.url.trim() : "";

      return !item?.id && (title || desc || url);
    });

    setCanAddNew(!unsavedItem);
    //   refreshUnsavedState();
  };


  useEffect(() => {
    const timer = setTimeout(() => {
      const ourClientEmpty = tabData?.OUR_CLIENT?.length === 0;
      const ourStoryEmpty = tabData?.OUR_STORY?.length === 0;

      if (ourClientEmpty || ourStoryEmpty) {
        setHasUnsavedItem(true);
      } else {
        setHasUnsavedItem(false);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [tabData?.OUR_CLIENT, tabData?.OUR_STORY]);


  const handleDelete = (whichTab, form, i, name, remove) => {

    const item = form.getFieldValue(whichTab)?.[i];
    const id = item?.id;

    if (id) {
      // Backend delete
      deleteSingleItem(whichTab, id, form);
    } else {
      // UI-only delete (new row not yet saved)
      remove(name);
      setHasUnsavedItem(false);
      refreshUnsavedState();
    }

    // Optional state updates (only if you need them)
    setTimeout(() => {
      const currentList = form.getFieldValue(whichTab) || [];
      const unsavedItem = currentList.find(
        (it) =>
          !it?.id &&
          (it?.title?.trim() || it?.description?.trim())
      );

      setCanAddNew(!unsavedItem);
      setHasUnsavedItem(!!unsavedItem);
      refreshUnsavedState();
    }, 100);
  };


  useEffect(() => {
    form.resetFields();
    refreshUnsavedState();
    setTimeout(() => {
      setLoader(false);
    }, 1000);
  }, [tabData[whichTab]]);

  if (dataLoader || postLoader || loader) {
    return (
      <div className="data-loader">
        <Spin tip="Loading" size="large" />
      </div>
    );
  } else {
    return (
      <div>
        <Row className="rowBody" gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          {tabKey.split("/")[1] === whichTab ? (
            <>
              <Col className="gutter-row tabContent" span={24}>
                <Form.List
                  name={whichTab}
                  initialValue={
                    tabData &&
                      tabData[whichTab] &&
                      tabData[whichTab].length > 0
                      ? tabData[whichTab]
                      : [""]
                  }
                >
                  {(fields, { add, remove }) => (
                    <>
                      <div className="inputBody">
                        <div style={{ textAlign: "right" }}>
                          {hasUnsavedItem && (
                            <Button
                              className="cancelBtn"
                              type="button"
                              onClick={() => handleClear(fields, remove)} // ✅ fixed
                            >
                              Clear
                            </Button>
                          )}
                          <Button
                            className="greenBtn"
                            onClick={handleAddMore}
                          >
                            Add <PlusOutlined />
                          </Button>
                        </div>
                      </div>
                      {fields.map(({ key, name, ...restField }, i) => {
                        const formItem =
                          form.getFieldValue([whichTab, i]) || {};
                        const backendItem = tabData[whichTab]?.[i];
                        const hasTitle =
                          (formItem.title &&
                            formItem.title.trim() !== "") ||
                          (backendItem &&
                            backendItem.title &&
                            backendItem.title.trim() !== "");
                        const hasId = backendItem && backendItem.id;
                        return (
                          <div className="card-item">
                            <Col
                              className="gutter-row tabContent"
                              span={16}
                              style={{ paddingRight: "25px" }}
                            >
                              <div className="d-flex">
                                <Form.Item
                                  className="w-100"
                                  {...restField}
                                  label="Name/Title"
                                  name={[name, "title"]}
                                  labelCol={{ span: 5 }}
                                  rules={[
                                    {
                                      required: true,
                                      message: "Title is required",
                                    },
                                  ]}
                                >
                                  <Input
                                    placeholder="Enter Title"
                                    maxLength={40}
                                    showCount={{
                                      formatter: ({ count }) => `${count}/40`,
                                    }}
                                    onKeyDown={(e) => {
                                      const currentValue = form.getFieldValue([whichTab, name, "title"]) || "";

                                      // Ignore control keys
                                      if (
                                        e.key.length === 1 && // only normal characters
                                        currentValue.length >= 40
                                      ) {
                                        form.setFields([
                                          {
                                            name: [whichTab, name, "title"],
                                            errors: ["Title cannot exceed 40 characters"],
                                          },
                                        ]);
                                      }
                                    }}
                                    onBlur={() => {
                                      form.setFields([
                                        {
                                          name: [whichTab, name, "title"],
                                          errors: [],
                                        },
                                      ]);
                                    }}
                                  />
                                </Form.Item>

                                <Form.Item>
                                  <div className="remove-btn">
                                    <DeleteOutlined
                                      style={{ cursor: "pointer" }}
                                      title="Delete Team"
                                      onClick={() => {
                                        handleDelete(
                                          whichTab,
                                          form,
                                          i,
                                          name,
                                          remove
                                        ); // ✅ pass remove too
                                      }}
                                    />
                                  </div>
                                </Form.Item>
                              </div>

                              <div className="d-flex">
                                <Form.Item
                                  label="Description"
                                  {...restField}
                                  labelCol={{ span: 5 }}
                                  name={[name, "description"]}
                                  className="w-100"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Description is required",
                                    },
                                    {
                                      max: MAX_CHARACTERS - 1,
                                      message: `Description should be less than ${MAX_CHARACTERS} character`,
                                    },
                                  ]}
                                >
                                  <TextArea
                                    placeholder="Enter Description"
                                    rows={5}
                                    maxLength={MAX_CHARACTERS}
                                    showCount
                                  />
                                </Form.Item>
                              </div>

                              <div className="d-flex">
                                <Form.Item
                                  {...restField}
                                  className="custom-work"
                                  name={[name, "mediaLink", "mediaType"]}
                                  label="Video or Image"
                                  labelCol={{ span: 5 }}
                                >
                                  <Select
                                    name={[name, "mediaLink", "mediaType"]}
                                    onChange={(e) =>
                                      SelectChangeHandler(e, i)
                                    }
                                    placeholder="Select Option"
                                  >
                                    <Select.Option value="VIMEO_MEDIA">
                                      Add Vimeo Link
                                    </Select.Option>
                                    <Select.Option value="SOCIAL_MEDIA">
                                      Add Youtube Link
                                    </Select.Option>
                                    <Select.Option value="SYNTHESIA_MEDIA">
                                      Add Synthesia Link
                                    </Select.Option>

                                    <Select.Option value="IMAGE">
                                      Upload Image
                                    </Select.Option>
                                  </Select>
                                </Form.Item>
                                <Form.Item>
                                  <div className="remove-btn">
                                    <Tooltip
                                      title={
                                        "Upload Video link or image with size lesser than 2.5MB. For best results, use images and videos with a 16:9 aspect ratio"
                                      }
                                    >
                                      <QuestionCircleOutlined />
                                    </Tooltip>
                                  </div>
                                </Form.Item>
                              </div>
                              {findValue(i, "type") === "SOCIAL_MEDIA" ? (
                                <Form.Item
                                  label="Youtube Link"
                                  labelCol={{ span: 5 }}
                                  {...restField}
                                  name={[name, "mediaLink", "url"]}
                                >
                                  <Input />
                                </Form.Item>
                              ) : findValue(i, "type") === "VIMEO_MEDIA" ? (
                                <Form.Item
                                  label="Vimeo Link"
                                  labelCol={{ span: 5 }}
                                  {...restField}
                                  name={[name, "mediaLink", "url"]}
                                >
                                  <Input />
                                </Form.Item>
                              ) : findValue(i, "type") === "IMAGE" ? (
                                <Form.Item
                                  {...restField}
                                  label="Image"
                                  colon={false}
                                  name="media"
                                  className="experienceImg uploadedImage"
                                  labelCol={{ span: 5 }}
                                >
                                  {findValue(i, "blob") ? (
                                    <Image
                                      src={URL.createObjectURL(
                                        findValue(i, "blob")
                                      )}
                                      alt=""
                                    />
                                  ) : findValue(i, "url") ? (
                                    <Image
                                      src={findValue(i, "url")}
                                      alt=""
                                    />
                                  ) : (
                                    <Image src={UserIcon} alt="" />
                                  )}
                                  <div>
                                    <label
                                      name="file"
                                      className="fileUploadBtn greenBtn"
                                    >
                                      {" "}
                                      Upload image
                                      <input
                                        accept="image/png, image/gif, image/jpeg"
                                        type="file"
                                        // required={true}
                                        onChange={(e) =>
                                          updateImage(e, i, form, whichTab)
                                        }
                                      />

                                    </label>
                                    <div style={{ marginLeft: '12px', marginTop: '5px' }} className="">Add or Edit</div>
                                  </div>
                                </Form.Item>
                              ) : findValue(i, "type") ===
                                "SYNTHESIA_MEDIA" ? (
                                <Form.Item
                                  label="Synthesia Link"
                                  labelCol={{ span: 5 }}
                                  {...restField}
                                  name={[name, "mediaLink", "url"]}

                                >
                                  <Input />
                                </Form.Item>
                              ) : null}

                              <Form.Item
                                className="w-100"
                                style={{
                                  display: "flex",
                                  justifyContent: "end",
                                }}
                              >
                                {formChanged ? (
                                  <Button
                                    className="saveBtn greenBtn"
                                    onClick={handleSave}
                                  >
                                    Save
                                  </Button>
                                ) : (
                                  <Button
                                    className="saveBtn greenBtn"
                                    style={{
                                      opacity: "0.5",
                                      pointerEvents: "none",
                                    }}
                                  >
                                    Save
                                  </Button>
                                )}
                              </Form.Item>
                            </Col>
                            <Col className="gutter-row tabContent" span={8}>
                              <div>
                                {form.getFieldValue(whichTab)?.[i]
                                  ? renderMedia(
                                    form.getFieldValue(whichTab)[i]
                                  ) // ✅ form state is always in sync
                                  : null}
                              </div>
                            </Col>
                          </div>
                        );
                      })}
                    </>
                  )}
                </Form.List>
              </Col>
            </>
          ) : (
            <></>
          )}
        </Row>
      </div>
    );
  }
}
